import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Settings, Info, BarChart3, ClipboardCheck } from 'lucide-react';
import { Tabs, TabsList } from '@/components/ui/tabs';
import { useQueryState } from 'hooks/useQueryState';
import { useExperiment } from '@/state/experiments/useExperiment';
import { isConcluded } from '@/state/experiments/report-questions';
import { useReportWizard } from '@/state/experiments/useReportWizard';
import { useResultsAi } from '@/state/results-ai/useResultsAi';
import { useTourScreen } from '@/tours';
import { isPreStart } from 'helpers/experiment-status';
import { Header } from './Header';
import { SCREEN_TABS_LIST_CLASS, ScreenTab } from './ScreenTab';
import { ResultsAiAskField, ResultsAiPanel } from './ResultsAi';
import { Timeline } from './Timeline';
import { ExperimentSettingsScreen } from './ExperimentSettings';
import { ExperimentInfoScreen } from './ExperimentInfo';
import { ExperimentStatisticsScreen } from './ExperimentStatistics';
import { ExperimentReportScreen } from './ExperimentReport';
import { ReportWizard } from './ExperimentReport/ReportWizard';

export type Screens = 'settings' | 'statistics' | 'info' | 'report' | '';

/**
 * Keyed by the experiment, so a link from one experiment to another (a similar past test, a
 * notification) starts the screen afresh: the results chat's running turn on the first is stopped,
 * and its draft and state are not carried over (the route alone keeps the component mounted).
 */
export const ExperimentDetailsScreen = () => {
  const { id } = useParams();
  return <ExperimentDetails key={id} />;
};

const ExperimentDetails = () => {
  const { id } = useParams();
  const [screen, setScreen] = useQueryState<Screens>('screen', '');
  const { experiment } = useExperiment(+id!);
  // A draft in QA has no results yet either: it shows what a draft shows.
  const isDraft = isPreStart(experiment?.status);
  const hasReportTab = isConcluded(experiment?.status);
  // Offered by the two actions that conclude a test on screen: End in the header and Deploy on
  // the Statistics tab. Owned here, not in either, so both open the one wizard and it lands on
  // the Debrief tab when saved.
  const reportWizard = useReportWizard(+id!);
  // Owned here, not by a tab: asked from the header on every tab, and a running answer survives a
  // tab change. On a draft too (decided 2026-10-20): the set-up and the brief are worth asking
  // about before it starts, and the panel greets a draft for that.
  const resultsAi = useResultsAi({ experimentId: +id! });
  const showResultsAi = resultsAi.available;
  // Tabs are written with replaceState, which the router never sees, so the open tab is
  // reported to the tour engine: each tab has a tour of its own.
  useTourScreen(screen || null);

  useEffect(() => {
    // A stale ?screen=report on an experiment with no Debrief tab falls back to the default,
    // the same as an empty one, instead of rendering a tab strip with nothing selected.
    const staleReport = screen === 'report' && !hasReportTab;
    if (experiment && (!screen || staleReport)) {
      const newScreen = isDraft ? 'settings' : 'statistics';
      setScreen(newScreen);
    }
  }, [experiment, screen, isDraft, hasReportTab, setScreen]);

  if (!experiment || !screen) return null;

  return (
    <div className="flex flex-col gap-[14px]">
      <Header
        experiment={experiment}
        screen={screen}
        onEnded={reportWizard.offer}
        ask={showResultsAi ? <ResultsAiAskField manager={resultsAi} /> : null}
      />
      {showResultsAi && resultsAi.open && (
        <ResultsAiPanel manager={resultsAi} viewLabel={resultsAi.viewLabel} draft={isDraft} />
      )}
      <Timeline experiment={experiment} />

      <Tabs value={screen} onValueChange={(value) => setScreen(value as Screens)}>
        <TabsList className={SCREEN_TABS_LIST_CLASS}>
          <ScreenTab value="settings" icon={<Settings className="size-3.5" strokeWidth={1.5} />} label="Settings" />
          {/* The value stays 'info' so existing ?screen=info links keep working. The tab holds
              the case for the test (importance, plan, hypothesis, notes), which "Info" did not say. */}
          <ScreenTab value="info" icon={<Info className="size-3.5" strokeWidth={1.5} />} label="Brief" />
          <ScreenTab
            value="statistics"
            icon={<BarChart3 className="size-3.5" strokeWidth={1.5} />}
            label="Statistics"
          />
          {/* Ended and Deployed both: deploying a winner is also the end of the test. */}
          {hasReportTab && (
            <ScreenTab
              value="report"
              icon={<ClipboardCheck className="size-3.5" strokeWidth={1.5} />}
              label="Debrief"
            />
          )}
        </TabsList>
      </Tabs>

      {screen === 'statistics' && isDraft && (
        <section className="bg-panel border-line rounded-panel border p-4">
          <p className="supporting">
            Statistics appear once this experiment is deployed and starts collecting traffic. How long it needs to run
            is planned on the Brief tab.
          </p>
        </section>
      )}

      {screen === 'settings' && <ExperimentSettingsScreen experiment={experiment} />}
      {screen === 'info' && <ExperimentInfoScreen experiment={experiment} />}
      {screen === 'statistics' && !isDraft && (
        <ExperimentStatisticsScreen experiment={experiment} onDeployed={reportWizard.offer} />
      )}
      {screen === 'report' && hasReportTab && <ExperimentReportScreen key={experiment.id} experiment={experiment} />}

      {reportWizard.isOpen && (
        <ReportWizard
          key={experiment.id}
          experiment={experiment}
          onClose={reportWizard.close}
          onViewReport={() => setScreen('report')}
        />
      )}
    </div>
  );
};
